import { useState, type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Card, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";

import { type SentEmailSummary } from "@fitness-app/data-models/entities/SentEmail";
import { CloudFunctions } from "@fitness-app/data-models/http/registry";

import { callFn } from "@fitness-app/app-store/src/helpers/callFn";

import { traineeKeys, useTraineeId, useTraineeProfile } from "~/modules/Trainee/TraineeProfile/scope";
import { traineeApi } from "~/store/initializeStore";
import { emailStatusColors, emailStatusLabels, emailTemplateLabels } from "./emailLabels";
import SentEmailPreviewDrawer from "./SentEmailPreviewDrawer";

const PAGE_SIZE = 25;

const TraineeEmails: FunctionComponent = () => {
  const traineeId = useTraineeId();
  const email = useTraineeProfile().data?.email;
  const [page, setPage] = useState(1);
  const [previewedMessageId, setPreviewedMessageId] = useState<string | null>(null);

  const emailsQuery = useQuery({
    queryKey: [...traineeKeys.all(traineeId), "sentEmails", page],
    keepPreviousData: true,
    queryFn: async () => {
      const { data } = await callFn(traineeApi, CloudFunctions.trainee.routes.getSentEmails, undefined, {
        params: { traineeId },
        query: { count: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
      });

      return data;
    },
  });

  return (
    <Card
      title="Wysłane maile"
      // Postmark trzyma skrzynkę nadawczą 45 dni — bez tego zdania „nic tu nie ma"
      // czyta się jak „nie wysłaliśmy nic", a nie „to poza oknem retencji".
      extra={
        <Typography.Text type="secondary">
          {email ? `${email} · ` : ""}
          historia z ostatnich 45 dni
        </Typography.Text>
      }
    >
      {emailsQuery.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Nie udało się pobrać maili z Postmarka"
          description="Spróbuj odświeżyć za chwilę. Jeśli błąd się powtarza, sprawdź status Postmarka."
        />
      )}

      <Table<SentEmailSummary>
        dataSource={emailsQuery.data?.messages ?? []}
        rowKey="messageId"
        size="middle"
        scroll={{ x: true }}
        loading={emailsQuery.isFetching}
        locale={{ emptyText: "Brak maili wysłanych w ostatnich 45 dniach" }}
        onRow={(row) => ({ onClick: () => setPreviewedMessageId(row.messageId), style: { cursor: "pointer" } })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: emailsQuery.data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
      >
        <Table.Column<SentEmailSummary>
          title="Data"
          dataIndex="sentAt"
          key="sentAt"
          width={160}
          render={(sentAt: string) => (sentAt ? dayjs(sentAt).format("DD.MM.YYYY HH:mm") : "-")}
        />
        <Table.Column<SentEmailSummary>
          title="Temat"
          dataIndex="subject"
          key="subject"
          render={(subject: string) => <Typography.Text strong>{subject || "(brak tematu)"}</Typography.Text>}
        />
        <Table.Column<SentEmailSummary>
          title="Rodzaj"
          dataIndex="tag"
          key="tag"
          width={240}
          // Tag nadajemy przy wysyłce (alias szablonu), więc maile sprzed TRE-1715
          // zostaną bez rodzaju — historii w Postmarku nie otagujemy wstecz.
          render={(tag: string | null) => (tag ? <Tag>{emailTemplateLabels[tag] ?? tag}</Tag> : "-")}
        />
        <Table.Column<SentEmailSummary>
          title="Status"
          dataIndex="status"
          key="status"
          width={140}
          render={(status: string) => (
            <Tag color={emailStatusColors[status] ?? "default"}>{emailStatusLabels[status] ?? status}</Tag>
          )}
        />
      </Table>

      <SentEmailPreviewDrawer
        traineeId={traineeId}
        messageId={previewedMessageId}
        onClose={() => setPreviewedMessageId(null)}
      />
    </Card>
  );
};

export default TraineeEmails;
